import React, { useState, useEffect, useCallback } from 'react';
import api from '@/api';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Loader2, Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { FormField } from './FormField';

const EXTRA_TYPES = [
  { value: 'catalog', label: 'Catalogo (selezione qty)' },
  { value: 'on_request', label: 'Su richiesta' },
];

export default function Extras() {
  const [properties, setProperties] = useState([]);
  const [selectedPropId, setSelectedPropId] = useState('');
  const [extras, setExtras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/properties').then(r => {
      setProperties(r.data);
      if (r.data.length > 0) setSelectedPropId(r.data[0].id);
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!selectedPropId) return;
    setLoading(true);
    try {
      const res = await api.get(`/extras?property_id=${selectedPropId}`);
      setExtras(res.data);
    } catch { } finally { setLoading(false); }
  }, [selectedPropId]);

  useEffect(() => { load(); }, [load]);

  const EMPTY = {
    property_id: selectedPropId, category: '', name: '', variant: '',
    size: '', price: 0, price_4pax: '', extra_type: 'catalog',
    is_active: true, sort_order: 0,
  };

  const openCreate = () => { setEditing(null); setForm({ ...EMPTY, property_id: selectedPropId }); setPanelOpen(true); };
  const openEdit = (e) => { setEditing(e.id); setForm({ ...EMPTY, ...e }); setPanelOpen(true); };

  const save = async () => {
    if (!form.name) { toast.error('Nome obbligatorio'); return; }
    setSaving(true);
    try {
      const payload = { ...form, price_4pax: form.price_4pax ? parseFloat(form.price_4pax) : null };
      if (editing) {
        await api.put(`/extras/${editing}`, payload);
      } else {
        await api.post('/extras', payload);
      }
      toast.success('Extra salvato');
      setPanelOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm('Eliminare?')) return;
    await api.delete(`/extras/${id}`);
    load();
  };

  const byCategory = extras.reduce((acc, e) => {
    const cat = e.category || 'Altro';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(e);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="label-gold">Gestione</p>
          <h2 className="font-serif text-3xl text-white font-light mt-1">Extra & Bevande</h2>
        </div>
        <button onClick={openCreate} className="btn-gold flex items-center gap-2" data-testid="new-extra-btn">
          <Plus size={14} /> Nuovo Extra
        </button>
      </div>
      <select className="input-dark max-w-xs" value={selectedPropId} onChange={e => setSelectedPropId(e.target.value)}>
        {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      {loading ? <div className="flex justify-center py-12"><Loader2 size={28} className="animate-spin text-[#c9a84c]" /></div> : (
        Object.keys(byCategory).length === 0 ? (
          <div className="card-dark rounded-lg p-10 text-center"><p className="text-[var(--lux-text-muted-3)] text-sm">Nessun extra. Aggiungine uno.</p></div>
        ) : Object.entries(byCategory).map(([cat, items]) => (
          <div key={cat} className="card-dark rounded-lg overflow-hidden">
            <div className="px-5 py-3 border-b border-[var(--lux-border-1)]">
              <p className="label-gold text-xs">{cat}</p>
            </div>
            <table className="w-full table-dark">
              <thead><tr>
                <th className="text-left">Nome</th>
                <th className="text-left hidden sm:table-cell">Variante / Size</th>
                <th className="text-right">Prezzo</th>
                <th className="text-right hidden md:table-cell">4pax</th>
                <th className="text-left">Tipo</th>
                <th />
              </tr></thead>
              <tbody>
                {items.map(e => (
                  <tr key={e.id}>
                    <td className="text-white">{e.name}</td>
                    <td className="hidden sm:table-cell text-[var(--lux-text-muted-2)] text-xs">{[e.variant, e.size].filter(Boolean).join(' · ') || '—'}</td>
                    <td className="text-right text-[#c9a84c]">€{e.price}</td>
                    <td className="text-right hidden md:table-cell text-[var(--lux-text-muted-2)] text-xs">{e.price_4pax ? `€${e.price_4pax}` : '—'}</td>
                    <td className="text-[var(--lux-text-muted-2)] text-xs">{e.extra_type}</td>
                    <td>
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => openEdit(e)} className="p-1.5 text-[var(--lux-text-muted-3)] hover:text-white"><Edit size={13} /></button>
                        <button onClick={() => remove(e.id)} className="p-1.5 text-[var(--lux-text-muted-3)] hover:text-red-400"><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}
      <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
        <SheetContent className="bg-[var(--lux-surface-1)] border-l border-[#222] text-white w-full sm:max-w-md overflow-y-auto">
          <SheetHeader className="mb-6">
            <SheetTitle className="font-serif text-2xl font-light text-white">{editing ? 'Modifica' : 'Nuovo'} Extra</SheetTitle>
          </SheetHeader>
          <div className="space-y-4">
            <FormField label="Nome *" name="name" form={form} setForm={setForm} />
            <FormField label="Categoria" name="category" form={form} setForm={setForm} />
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Variante" name="variant" form={form} setForm={setForm} />
              <FormField label="Formato/Size" name="size" form={form} setForm={setForm} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Prezzo €" name="price" type="number" form={form} setForm={setForm} />
              <FormField label="Prezzo 4pax €" name="price_4pax" type="number" form={form} setForm={setForm} />
            </div>
            <FormField label="Tipo" name="extra_type" options={EXTRA_TYPES} form={form} setForm={setForm} />
            <div className="flex gap-3 pt-4 border-t border-[#222]">
              <button onClick={save} disabled={saving} className="btn-gold flex-1 flex items-center justify-center gap-2">
                {saving && <Loader2 size={13} className="animate-spin" />}
                Salva
              </button>
              <button onClick={() => setPanelOpen(false)} className="btn-outline-gold flex-1">Annulla</button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
